import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  Users2,
  Bell,
  Kanban,
  Hammer,
  Building2,
  Truck,
  Receipt,
  UserCog,
  LogOut,
  Sun,
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard", roles: ["admin", "sales", "project_manager", "technician"] },
  { to: "/leads", label: "Leads", icon: Users2, tid: "nav-leads", roles: ["admin", "sales"] },
  { to: "/follow-ups", label: "Follow-ups", icon: Bell, tid: "nav-followups", roles: ["admin", "sales", "project_manager"] },
  { to: "/deals", label: "Deals", icon: Kanban, tid: "nav-deals", roles: ["admin", "sales"] },
  { to: "/projects", label: "Projects", icon: Hammer, tid: "nav-projects", roles: ["admin", "project_manager", "technician"] },
  { to: "/customers", label: "Customers", icon: Building2, tid: "nav-customers", roles: ["admin", "sales", "project_manager"] },
  { to: "/vendors", label: "Vendors & POs", icon: Truck, tid: "nav-vendors", roles: ["admin", "project_manager"] },
  { to: "/invoices", label: "Invoices", icon: Receipt, tid: "nav-invoices", roles: ["admin", "sales"] },
  { to: "/users", label: "Team", icon: UserCog, tid: "nav-users", roles: ["admin"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = () => {
    logout();
    navigate("/login");
  };
  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <aside className="sidebar w-64 shrink-0 min-h-screen flex flex-col text-slate-100" data-testid="app-sidebar">
      <div className="px-5 pt-6 pb-5 flex items-center gap-3 border-b border-white/10">
        <div className="w-10 h-10 rounded-xl bg-[#10B981]/20 border border-[#10B981]/40 flex items-center justify-center">
          <Sun className="w-5 h-5 text-[#34D399]" />
        </div>
        <div>
          <div className="font-display font-semibold leading-tight">SunTrack CRM</div>
          <div className="text-[11px] uppercase tracking-widest text-slate-300">Solar EPC</div>
        </div>
      </div>

      <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto thin-scroll">
        {nav
          .filter((n) => n.roles.includes(user?.role))
          .map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-white font-medium"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[#10B981] text-white flex items-center justify-center text-sm font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-300 truncate">
              {user?.role?.replace("_", " ")}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
